import { all, takeEvery, call, put, select } from 'redux-saga/effects';
import { fetchEntity } from '@classpass/saga-tools';
import React from 'react';
import { defineMessages } from 'react-intl';
import { SMARTSPOT_REASON } from 'constants/logic';
import LINKS from 'constants/links';
import { venueSmartspotData } from 'store/saga/entities/bound';
import { selectActiveVenueId, selectVenueSmartspotData } from 'selectors/general';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { closeModal, waitForModal } from 'components/Modal';
import { reportError } from 'helpers/error-logging';
import { trackSmartspotClassLevelChange } from 'helpers/events';

export const FETCH_VENUE_SMARTSPOT_DATA = 'FETCH_VENUE_SMARTSPOT_DATA';
export const UPDATE_SMARTSPOT = 'UPDATE_SMARTSPOT';

export const fetchVenueSmartspotData = (venueId, startDate) => ({
  type: FETCH_VENUE_SMARTSPOT_DATA,
  venueId,
  startDate,
});
export const updateSmartSpot = (values, meta, asyncFns = {}) => ({ type: UPDATE_SMARTSPOT, values, meta, asyncFns });

const messages = {
  success: defineMessages({
    venueEnabled: {
      id: 'smartspot.saga.display-smartspot-success-banner.enabled-venue',
      defaultMessage: 'SmartSpot is turned on for all future classes at {venueName}.',
    },
    venueDisabled: {
      id: 'smartspot.saga.display-smartspot-success-banner.disabled-venue',
      defaultMessage: 'SmartSpot is turned off for all future classes at {venueName}.',
    },
    scheduleEnabled: {
      id: 'smartspot.saga.display-smartspot-success-banner.enabled-schedule',
      defaultMessage:
        'SmartSpot is turned on for the {startDateTime, date, time} {className}, {startDateTime, date, long-w-dow} class.',
    },
    scheduleDisabled: {
      id: 'smartspot.saga.display-smartspot-success-banner.disabled-schedule',
      defaultMessage:
        'SmartSpot is turned off for the {startDateTime, date, time} {className}, {startDateTime, date, long-w-dow} class.',
    },
    recurringEnabled: {
      id: 'smartspot.saga.display-smartspot-success-banner.enabled-recurring',
      defaultMessage:
        'SmartSpot is turned on for the {startDateTime, date, time} {className}, {startDateTime, date, long-w-dow} class and all future instances of this class.',
    },
    recurringDisabled: {
      id: 'smartspot.saga.display-smartspot-success-banner.disabled-recurring',
      defaultMessage:
        'SmartSpot is off for all future classes of {className}. <a>Learn how you can fill this class</a>.',
    },
    genericUpdate: {
      id: 'smartspot.saga.display-smartspot-success-banner.generic-update',
      defaultMessage: 'SmartSpot settings for {venueName} have been updated.',
    },
  }),
  error: defineMessages({
    fetchingSmartspot: {
      id: 'smartspot.saga.do-fetch-venue-smartspot-data.error',
      defaultMessage: 'There was a problem fetching SmartSpot data.',
    },
    updatingSmartspot: {
      id: 'smartspot.saga.dp-update-smartspot.error',
      defaultMessage: 'There was a problem updating your SmartSpot settings.',
    },
  }),
};

export function* doFetchVenueSmartspotData(client, action) {
  try {
    const { venueId, startDate = new Date() } = action;
    yield call(fetchEntity, venueSmartspotData, [client, { meta: { venueId, date: startDate } }]);
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.fetchingSmartspot)), call(reportError, error)]);
  }
}

export function* displaySmartSpotSuccessBanner({ isEnabled, reason, venueName, className, startDateTime }) {
  let messageData;

  if (reason === SMARTSPOT_REASON.GENERIC_UPDATE) {
    messageData = [messages.success.genericUpdate, { venueName }];
  } else if (reason === SMARTSPOT_REASON.VENUE_SETTING) {
    if (isEnabled) {
      messageData = [messages.success.venueEnabled, { venueName }];
    } else {
      messageData = [messages.success.venueDisabled, { venueName }];
    }
  } else if (reason === SMARTSPOT_REASON.SCHEDULE_SETTING) {
    if (isEnabled) {
      messageData = [messages.success.scheduleEnabled, { className, startDateTime }];
    } else {
      messageData = [messages.success.scheduleDisabled, { className, startDateTime }];
    }
  } else if (reason === SMARTSPOT_REASON.RECURRING_RULE) {
    if (isEnabled) {
      messageData = [messages.success.recurringEnabled, { className, startDateTime }];
    } else {
      messageData = [
        messages.success.recurringDisabled,
        {
          className,
          a: text => (
            <a href={LINKS.smartspot.learnMore} target="_blank" rel="noopener noreferrer">
              {text}
            </a>
          ),
        },
      ];
    }
  }

  if (messageData) {
    yield put(createInfoBanner(...messageData));
  }
}

export function* doUpdateSmartSpot(dependencies, { values, meta, asyncFns }) {
  try {
    const { isEnabled } = values;
    const { isModal, requiresConfirmation, venueName, venueSubtitle, classId, scheduleId } = meta;
    let { venueId } = meta;
    const { resolveSubmit } = asyncFns;
    const { scheduleData, ...smartSpotDataFromState } = yield select(selectVenueSmartspotData);
    const smartSpotData = { ...smartSpotDataFromState, ...values };

    if (!venueId) {
      venueId = yield select(selectActiveVenueId);
    }

    let confirmed;
    if (requiresConfirmation) {
      confirmed = yield call(waitForModal, dependencies, 'TurnOnSmartSpotConfirmationModal', {
        venueName,
        venueSubtitle,
      });

      if (!confirmed) {
        return;
      }
    }

    if (!requiresConfirmation || (requiresConfirmation && confirmed)) {
      yield call(fetchEntity, venueSmartspotData, [dependencies, { data: smartSpotData, meta }]);
      if (isModal) {
        yield put(closeModal());
      }

      yield call(displaySmartSpotSuccessBanner, { ...smartSpotData, ...meta });

      if (classId || scheduleId) {
        yield call(trackSmartspotClassLevelChange, {
          isEnabled,
          classId,
          scheduleId,
        });
      }
    }

    if (resolveSubmit) {
      yield call(resolveSubmit);
    }
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.updatingSmartspot)), call(reportError, error)]);
  }
}

export function* watchActions(client) {
  yield takeEvery(FETCH_VENUE_SMARTSPOT_DATA, doFetchVenueSmartspotData, client);
  yield takeEvery(UPDATE_SMARTSPOT, doUpdateSmartSpot, client);
}
